import { useSelector } from 'react-redux';
import type { ReactNode, VoidFunctionComponent } from 'react';

import { CodeSentryFetcherName, fetcherToPlainText, isLinuxFetcher } from '../../../../../../shared/fetchers';
import { IgnoredKind } from '../../../../../../shared/issuesV2';
import { parseLinuxPackage } from '../../../../../../shared/linux';
import Locator from '../../../../../../shared/Locator';
import { useGetDependencyConfidence } from '../../../../../hooks/api';
import * as IssuesV2Store from '../../../../../redux/issuesV2';
import { Badge, BadgeColors } from '../../../../generic';
import BinaryComponentBadge from '../../../../shared/revisions/BinaryComponentBadge';
import DepthIndicator from '../DepthIndicator';
import FoundDateBadge from '../FoundDateBadge';
import IgnoredIssueBadge from '../IgnoredIssueBadge';
import IgnoredKindBadge from '../IgnoredKindBadge';
import IssueTicketBadge from '../IssueTicketBadge';
import IssueTitle from '../IssueTitle';
import ProjectsList from '../ProjectsList';
import WithSeparator from '../WithSeparator';
import type { Fetcher } from '../../../../../../shared/fetchers';
import type { DependencyUsage, IgnoredKindMetadata, Issue, JiraTicket } from '../../../../../../shared/issuesV2';
import type { LinuxPackageParts } from '../../../../../../shared/linux';
import type { ReduxStore } from '../../../../../redux/store';

import BaseIssueItemContent from './BaseIssueItemContent';

type Props = {
  issue: Issue;
  locator: string;
  title?: string;
  dependency: string;
  version: string | null;
  tickets?: JiraTicket[];
  badge: ReactNode;
  packageManager: string;
  depths: Record<DependencyUsage, number>;
  createdAt: string;
  ignoredAt?: string;
  ignoredKinds?: IgnoredKindMetadata[];
};

/**
 * This component displays the content of the licensing or quality issue row.
 */
const NonVulnerabilityIssueItemContent: VoidFunctionComponent<Props> = ({
  issue,
  locator,
  title,
  dependency,
  version,
  tickets,
  packageManager,
  depths,
  createdAt,
  ignoredAt,
  ignoredKinds,
  badge,
}) => {
  const grouping = useSelector(IssuesV2Store.selectors.getGrouping);
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const issueDetailsURL = useSelector((state: ReduxStore) =>
    IssuesV2Store.selectors.getIssueDetailsURL(state, issue.id));
  const isLinuxDep = isLinuxFetcher(packageManager);

  const projectRevision = scope.type === 'project' ? `${scope.id}$${scope.revision}` : '';
  const { confidence, isLoading } = useGetDependencyConfidence(projectRevision, locator);
  const dependencyLocator = Locator.parse(locator);
  const isBinaryComponent = dependencyLocator.fetcher === CodeSentryFetcherName;

  const rawPackageName = Locator.parse(locator).package || dependency;
  // the output of parseLinuxPackage is guaranteed to have distroVersion because we are
  // checking if the input is a linux dep, which must have name, distro, and version within it
  const osVersion = isLinuxDep ? ` (${(parseLinuxPackage(rawPackageName) as LinuxPackageParts).distroVersion})` : '';

  // The projects to use for the projects list.  The empty list disables it if we aren't in the global scope.
  const projects = scope.type === 'global' ? issue.projects : [];

  // Whether we are rendering in a grouped view or not for choosing what to render.
  const isUngrouped = grouping === 'issue';

  const shouldDisplayConfidenceBadage = isBinaryComponent && isUngrouped;
  const shouldDisplayPackageManagerBadge = packageManager && isUngrouped && !shouldDisplayConfidenceBadage;

  return (
    <BaseIssueItemContent
      headerSlot={
        <>
          {badge}
          <IssueTitle
            title={title}
            packageManager={packageManager}
            dependency={dependency}
            version={version}
            issueDetailsURL={issueDetailsURL}
          />
        </>
      }
      contentSlot={
        <>
          {ignoredAt && <IgnoredIssueBadge ignoredAt={ignoredAt} />}
          {tickets && (tickets?.length ?? 0) > 0 && <IssueTicketBadge tickets={tickets} />}
          {shouldDisplayConfidenceBadage && (
            <BinaryComponentBadge confidence={confidence} isLoading={isLoading} />
          )}
          {shouldDisplayPackageManagerBadge && (
            <WithSeparator>
              <Badge color={BadgeColors.White}>
                {fetcherToPlainText(packageManager as Fetcher) || packageManager}
                {osVersion}
              </Badge>
            </WithSeparator>
          )}
          {depths && isUngrouped && <DepthIndicator depths={depths} />}
          {projects && isUngrouped && projects.length > 0 && (
            <WithSeparator>
              {' '}
              <ProjectsList projects={projects} />{' '}
            </WithSeparator>
          )}
          {createdAt && <FoundDateBadge createdAt={createdAt} />}
        </>
      }
      contentRightSlot={
        ignoredKinds &&
        !!ignoredKinds?.length &&
        ignoredKinds.some((ignore) => ignore.ignoreType === IgnoredKind.IssueException) && (
          <IgnoredKindBadge ignoredKinds={ignoredKinds} numProjects={projects.length} />
        )
      }
    />
  );
};

export default NonVulnerabilityIssueItemContent;
